import type { JSX } from "react";
import { Outlet, useMatches } from "react-router";
import { Stack, Box, Button } from "@mui/material";
import { useAuth, useAutoSignin } from "react-oidc-context";
import NavBar from "@/ui/nav-bar";
import ErrorAlert from "@/ui/error-alert";
import RequirePermissions from "@/auth/require-permissions";
import LoadingIndicator from "@/ui/loading-indicator";

function wantsBackButton(handle: unknown): boolean {
  return (
    typeof handle === "object" &&
    handle !== null &&
    "backButton" in handle &&
    handle.backButton === true
  );
}

function SignInFailed(): JSX.Element {
  const auth = useAuth();
  return (
    <Box p={3}>
      <ErrorAlert
        message="Sign-in failed."
        action={
          <Button color="inherit" onClick={() => void auth.signinRedirect()}>
            Try again
          </Button>
        }
      />
    </Box>
  );
}

/** Nothing beneath it renders, or queries, until the caller is signed in. */
export default function AuthenticatedLayout(): JSX.Element {
  const { isAuthenticated, error } = useAutoSignin();
  const matches = useMatches();
  const backButton = matches.some((m) => wantsBackButton(m.handle));

  if (error) return <SignInFailed />;
  if (!isAuthenticated) return <LoadingIndicator />;

  return (
    <Stack height="100%">
      <NavBar backButton={backButton} />
      <Box flex={1} minHeight={0}>
        <RequirePermissions>
          <Outlet />
        </RequirePermissions>
      </Box>
    </Stack>
  );
}
